
import { ElForm, ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import UploadFormItem from '@/components/form/common/UploadFormItem.vue'
import {
  FontsModel,
  FontsPageSearchModel,
  FontsSearchModel,
  FontsAddModel,
  FontsUpdateModel,
  FontsUpdateFileModel,
  IdsModel
} from '@/model/product/FontsModel'
import {
  pageSearchFonts,
  addFonts,
  updateFonts,
  removeFonts,
  updateFontsFile,
  batchRemoveFonts
} from '@/apis/product/fonts'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    UploadFormItem,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const fontFileUrlRef = ref()
    const fontThumbnailRef = ref()
    const updateFontFileUrlRef = ref()
    const updateFontThumbnailRef = ref()
    const targetLanguages = [
      { label: t('fonts.all'), value: 'all' },
      { label: t('fonts.zhCn'), value: 'zh_cn' },
      { label: t('fonts.zhTw'), value: 'zh_tw' },
      { label: t('fonts.en'), value: 'en' },
      { label: t('fonts.ja'), value: 'ja' },
      { label: t('fonts.ko'), value: 'ko' },
      { label: t('fonts.de'), value: 'de' },
      { label: t('fonts.fr'), value: 'fr' },
      { label: t('fonts.it'), value: 'it' },
      { label: t('fonts.es'), value: 'es' },
      { label: t('fonts.ar'), value: 'ar' },
      { label: t('fonts.ru'), value: 'ru' },
      { label: t('fonts.nl'), value: 'nl' },
      { label: t('fonts.pt'), value: 'pt' },
      { label: t('fonts.pl'), value: 'pl' },
      { label: t('fonts.th'), value: 'th' },
      { label: t('fonts.cs'), value: 'cs' },
      { label: t('fonts.sk'), value: 'sk' },
      { label: t('fonts.el'), value: 'el' },
      { label: t('fonts.hu'), value: 'hu' },
      { label: t('fonts.bg'), value: 'bg' },
      { label: t('fonts.tr'), value: 'tr' },
      { label: t('fonts.ro'), value: 'ro' }
    ]
    const searchStruct = [
      { prop: 'fontName', label: t('fonts.fontName'), placeholder: t('fonts.fontNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'targetLanguage', label: t('fonts.targetLanguage'), placeholder: t('fonts.targetLanguageP'), component: 'el-select', type: 'text', model: null, options: targetLanguages }
    ]

    const createStruct = [
      { prop: 'fontName', label: t('fonts.fontName'), placeholder: t('fonts.fontNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'targetLanguage', label: t('fonts.targetLanguage'), placeholder: t('fonts.targetLanguageP'), component: 'el-select', type: 'text', model: null, options: targetLanguages }
    ]

    const updateStruct = [
      { prop: 'fontName', label: t('fonts.fontName'), placeholder: t('fonts.fontNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'targetLanguage', label: t('fonts.targetLanguage'), placeholder: t('fonts.targetLanguageP'), component: 'el-select', type: 'text', model: null, options: targetLanguages }
    ]

    const updateFontStruct = [
      { prop: 'fontName', label: t('fonts.fontName'), placeholder: t('fonts.fontNameP'), component: 'el-input', type: 'text', model: null, readonly: true }
    ]

    const fontsRole = {
      fontName: [{ required: true, message: t('fonts.fontName').concat(t('common.isRequired')), trigger: 'blur' }],
      targetLanguage: [{ required: true, message: t('fonts.targetLanguage').concat(t('common.isRequired')), trigger: 'blur' }],
      fontFileUrl: [{ required: true, message: t('fonts.fontFileUrl').concat(t('common.isRequired')), trigger: 'blur' }],
      fontThumbnail: [{ required: true, message: t('fonts.fontThumbnail').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [
      { prop: 'fontName', label: t('fonts.fontName'), sortable: 'custom' },
      { prop: 'fontFileUrl', label: t('fonts.fontFileUrl'), sortable: 'custom', link: true },
      { prop: 'fontThumbnail', label: t('fonts.fontThumbnail'), sortable: 'custom', img: 'fontThumbnail' },
      { prop: 'targetLanguage', label: t('fonts.targetLanguage'), sortable: 'custom', formatter: (row: FontsModel) => targetLanguages.find(t => t.value === row.targetLanguage)?.label },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: FontsModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: FontsModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as FontsSearchModel,
      tableData: Array<FontsModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'fonts.edit',
        updateFont: 'fonts.editFont',
        create: 'fonts.create'
      },
      dialogFormVisible: false,
      fontsRole: fontsRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as FontsAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as FontsUpdateModel,
      updateFontModel: createModel(updateFontStruct) as FontsUpdateFileModel,
      updateFontStruct: updateFontStruct,
      editRow: {} as FontsModel,
      updateFontRow: {} as FontsModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: FontsPageSearchModel = {
          fontName: dataMap.searchFormModel.fontName,
          fontFileUrl: dataMap.searchFormModel.fontFileUrl,
          fontThumbnail: dataMap.searchFormModel.fontThumbnail,
          targetLanguage: dataMap.searchFormModel.targetLanguage,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchFonts(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      fontFileUrlChange(file: any) {
        dataMap.createModel.fontFileUrl = file
      },
      fontThumbnailChange(file: any) {
        dataMap.createModel.fontThumbnail = file
      },
      async createSubmit() {
        const params = new FormData()
        params.append('fontName', dataMap.createModel.fontName)
        params.append('fontFile', dataMap.createModel.fontFileUrl.raw)
        params.append('fontThumbnail', dataMap.createModel.fontThumbnail.raw)
        params.append('targetLanguage', dataMap.createModel.targetLanguage)
        const loading = ElLoading.service({
          lock: true,
          text: t('common.uploading'),
          background: 'rgba(0, 0, 0, 0.7)'
        })
        const result = await addFonts(params)
        loading.close()
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.status) {
          if (result.data.code === 0) {
            ElMessage({
              message: t('common.createSuccess'),
              type: 'success',
              duration: 2000
            })
          } else {
            ElMessage({
              message: result?.data.message,
              type: 'error',
              duration: 2000
            })
          }
        } else {
          ElMessage({
            message: result?.statusText,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: FontsUpdateModel = {
          id: dataMap.updateModel.id,
          fontName: dataMap.updateModel.fontName,
          targetLanguage: dataMap.updateModel.targetLanguage
        }
        const result = await updateFonts(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateFontFileUrlChange(file: any) {
        dataMap.updateFontModel.fontFileUrl = file
      },
      updateFontThumbnailChange(file: any) {
        dataMap.updateFontModel.fontThumbnail = file
      },
      async updateFontSubmit() {
        const params = new FormData()
        params.append('id', dataMap.updateFontModel.id.toString())
        params.append('fontFile', dataMap.updateFontModel.fontFileUrl.raw)
        params.append('fontThumbnail', dataMap.updateFontModel.fontThumbnail.raw)
        const loading = ElLoading.service({
          lock: true,
          text: t('common.uploading'),
          background: 'rgba(0, 0, 0, 0.7)'
        })
        const result = await updateFontsFile(params)
        loading.close()
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.status) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.statusText,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeFonts(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveFonts(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.fontName = dataMap.editRow.fontName
        dataMap.updateModel.targetLanguage = dataMap.editRow.targetLanguage
      },
      createReset() {
        console.log('create reset')
        dataMap.createModel.fontFileUrl = null
        dataMap.createModel.fontThumbnail = null
        if (fontFileUrlRef.value) {
          fontFileUrlRef.value.onReset()
        }
        if (fontThumbnailRef.value) {
          fontThumbnailRef.value.onReset()
        }
      },
      updateFontReset() {
        console.log('update font reset')
        dataMap.updateFontModel.id = dataMap.updateFontRow.id
        dataMap.updateFontModel.fontName = dataMap.updateFontRow.fontName
        dataMap.updateFontModel.fontFileUrl = null
        dataMap.updateFontModel.fontThumbnail = null
        if (updateFontFileUrlRef.value) {
          updateFontFileUrlRef.value.onReset()
        }
        if (updateFontThumbnailRef.value) {
          updateFontThumbnailRef.value.onReset()
        }
      }
    })
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: FontsModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.fontName = row.fontName
        dataMap.updateModel.targetLanguage = row.targetLanguage
        dataMap.dialogFormVisible = true
      }
    }
    const handleEditFont = (row?: FontsModel) => {
      if (row) {
        dataMap.updateFontRow = row
        dataMap.dialogStatus = 'updateFont'
        // dataMap.updateFontModel.id = row.id
        // dataMap.updateFontModel.fontName = row.fontName
        // dataMap.updateFontModel.fontFileUrl = row.fontFileUrl
        // dataMap.updateFontModel.fontThumbnail = row.fontThumbnail
        dataMap.updateFontReset()
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleEditFont,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      fontFileUrlRef,
      fontThumbnailRef,
      updateFontFileUrlRef,
      updateFontThumbnailRef,
      ...toRefs(dataMap)
    }
  }
})
